import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { subscribeToUserChats, listenMessages, markMessagesAsRead } from '../../services/messages.service';
import { doc, getDoc, collection, addDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { 
  MessageCircle, 
  Search, 
  Send, 
  MoreVertical,
  CheckCircle2,
  Building2,
  Paperclip,
  Smile
} from 'lucide-react';
import toast from 'react-hot-toast';

export default function TenantMessagesNew() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [otherUser, setOtherUser] = useState(null);
  const [property, setProperty] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const handledParamsRef = useRef('');
  const requestedThreadId = searchParams.get('threadId') || searchParams.get('chat');

  // Filter chats based on search
  const filteredChats = useMemo(() => {
    if (!searchQuery) return chats;
    
    const query = searchQuery.toLowerCase();
    return chats.filter(chat => {
      const otherParticipant = chat.participantsData?.find(p => p.uid !== user?.uid);
      const participantName = (otherParticipant?.displayName || otherParticipant?.name || otherParticipant?.email || '').toLowerCase();
      const lastMessage = (chat.lastMessage || '').toLowerCase();
      const propertyTitle = (chat.propertyTitle || '').toLowerCase();
      
      return participantName.includes(query) || 
             lastMessage.includes(query) ||
             propertyTitle.includes(query);
    });
  }, [chats, searchQuery, user]);

  // Load chats with property info
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const unsubscribe = subscribeToUserChats(user.uid, async (chatsData) => {
      // Sort chats by lastMessageAt (most recent first)
      const sortedChats = chatsData.sort((a, b) => {
        const dateA = a.lastMessageAt?.getTime ? a.lastMessageAt.getTime() : new Date(a.lastMessageAt || 0).getTime();
        const dateB = b.lastMessageAt?.getTime ? b.lastMessageAt.getTime() : new Date(b.lastMessageAt || 0).getTime();
        return dateB - dateA;
      });

      // Load property info for each chat
      const chatsWithPropertyInfo = await Promise.all(
        sortedChats.map(async (chat) => {
          if (chat.propertyId) {
            try {
              const propertyDoc = await getDoc(doc(db, 'koten', chat.propertyId));
              if (propertyDoc.exists()) {
                const propertyData = propertyDoc.data();
                return {
                  ...chat,
                  propertyTitle: propertyData.titel || propertyData.title || propertyData.naam || 'Pand',
                  propertyInfo: {
                    id: propertyDoc.id,
                    ...propertyData
                  }
                };
              }
            } catch (error) {
              console.error('Error loading property for chat:', chat.id, error);
            }
          }
          return chat;
        })
      );

      setChats(chatsWithPropertyInfo);
      setLoading(false);
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user]);

  useEffect(() => {
    if (!user || !requestedThreadId) return;

    if (handledParamsRef.current === requestedThreadId) return;
    handledParamsRef.current = requestedThreadId;

    const existingChat = chats.find((chat) => chat.id === requestedThreadId);
    if (existingChat) {
      setSelectedChat(existingChat);
      return;
    }

    (async () => {
      try {
        const chatDoc = await getDoc(doc(db, 'chats', requestedThreadId));
        if (chatDoc.exists()) {
          setSelectedChat({ id: chatDoc.id, ...chatDoc.data() });
        }
      } catch (error) {
        console.error('Error loading requested tenant chat:', error);
        toast.error('Kon het gesprek niet openen');
      }
    })();
  }, [user, requestedThreadId, chats]);

  useEffect(() => {
    if (chats.length === 0 || !selectedChat) return;
    const enriched = chats.find((chat) => chat.id === selectedChat.id);
    if (enriched && (!selectedChat.participantsData || selectedChat.participantsData.length === 0)) {
      setSelectedChat(enriched);
    }
  }, [chats, selectedChat]);

  // Load messages for selected chat + mark as read on every update
  useEffect(() => {
    if (!selectedChat?.id) {
      setMessages([]);
      return;
    }

    const chatId = selectedChat.id;
    const uid = user?.uid;

    // Mark read immediately when opening the chat
    if (uid) {
      markMessagesAsRead(chatId, uid).catch(err => {
        console.error('Error marking messages as read:', err);
      });
    }

    const unsubscribe = listenMessages(chatId, (messagesData) => {
      setMessages(messagesData);
      setTimeout(() => {
        if (messagesContainerRef.current) {
          scrollToBottom();
        }
      }, 100);

      // Mark new incoming messages as read while the chat is open
      if (uid) {
        const hasUnread = messagesData.some(m => m.senderId !== uid && m.read !== true);
        if (hasUnread) {
          markMessagesAsRead(chatId, uid).catch(err => {
            console.error('Error marking new messages as read:', err);
          });
        } else {
          updateDoc(doc(db, 'chats', chatId), {
            [`lastReadAtBy.${uid}`]: new Date(),
          }).catch(() => {});
        }
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [selectedChat?.id, user?.uid]);

  // Load other user and property info
  useEffect(() => {
    if (!selectedChat || !user) {
      setOtherUser(null);
      setProperty(null);
      return;
    }

    const loadChatDetails = async () => {
      const resolveName = (d) => d?.displayName || d?.name || d?.contactName ||
        (d?.firstName && d?.lastName ? `${d.firstName} ${d.lastName}` : null) ||
        (d?.firstName ? d.firstName : null) ||
        d?.companyName || d?.agencyProfile?.companyName || d?.email || null;

      let pData = selectedChat.participantsData;
      if (!pData || pData.length === 0) {
        const enriched = chats.find(c => c.id === selectedChat.id);
        if (enriched?.participantsData?.length > 0) {
          pData = enriched.participantsData;
        }
      }

      let otherParticipantId = selectedChat.participants?.find(id => id !== user.uid)
        || (selectedChat.participants?.length > 0 ? selectedChat.participants[0] : null)
        || selectedChat.landlordId || selectedChat.ownerId || selectedChat.tenantId;

      if (pData && pData.length > 0) {
        const other = pData.find(p => p.uid !== user.uid) || pData[0];
        if (other && resolveName(other)) {
          setOtherUser({ ...other, displayName: resolveName(other) });
          const pid = other.uid || otherParticipantId;
          if (pid) {
            getDoc(doc(db, 'users', pid)).then(snap => {
              if (snap.exists()) {
                const data = snap.data();
                setOtherUser(prev => ({ ...prev, uid: pid, ...data, displayName: resolveName(data) || prev?.displayName }));
              }
            }).catch(() => {});
          }
        } else if (otherParticipantId) {
          try {
            const snap = await getDoc(doc(db, 'users', otherParticipantId));
            if (snap.exists()) {
              const data = snap.data();
              setOtherUser({ id: snap.id, uid: otherParticipantId, ...data, displayName: resolveName(data) });
            }
          } catch (error) {
            console.error('Error loading other user:', error);
          }
        }
      } else if (otherParticipantId) {
        try {
          const snap = await getDoc(doc(db, 'users', otherParticipantId));
          if (snap.exists()) {
            const data = snap.data();
            setOtherUser({ id: snap.id, uid: otherParticipantId, ...data, displayName: resolveName(data) });
          }
        } catch (error) {
          console.error('Error loading other user:', error);
        }
      }

      // Get property info if propertyId exists
      if (selectedChat.propertyId) {
        try {
          const propertyDoc = await getDoc(doc(db, 'koten', selectedChat.propertyId));
          if (propertyDoc.exists()) {
            setProperty({
              id: propertyDoc.id,
              ...propertyDoc.data()
            });
          }
        } catch (error) {
          console.error('Error loading property:', error);
        }
      }
    };

    loadChatDetails();
  }, [selectedChat, user]);

  // Scroll to bottom (only within messages container, not the whole page)
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedChat || !user) return;

    try {
      const messageData = {
        senderId: user.uid,
        text: newMessage.trim(),
        type: 'text',
        createdAt: serverTimestamp(),
        read: false
      };

      await addDoc(collection(db, 'chats', selectedChat.id, 'messages'), messageData);
      
      // Update chat with last message
      await updateDoc(doc(db, 'chats', selectedChat.id), {
        lastMessage: newMessage.trim(),
        lastMessageAt: serverTimestamp(),
        lastSenderId: user.uid,
        [`lastReadAtBy.${user.uid}`]: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      setNewMessage('');
      scrollToBottom();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Fout bij verzenden bericht');
    }
  };

  // Format time
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    const now = new Date();
    const diffInMs = now - date;
    const diffInMinutes = Math.floor(diffInMs / 60000);
    const diffInHours = Math.floor(diffInMs / 3600000);
    const diffInDays = Math.floor(diffInMs / 86400000);

    if (diffInMinutes < 1) return 'Nu';
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInHours < 24) return `${diffInHours}u`;
    if (diffInDays < 7) return `${diffInDays}d`;
    
    return date.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short'
    });
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (messageDate.getTime() === today.getTime()) {
      return date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
    }
    
    return date.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUnreadCount = (chat) => {
    if (!chat || !user) return 0;

    if ((chat.deletedFor || []).includes(user.uid)) return 0;
    if ((chat.hiddenForUsers || {})[user.uid] === true) return 0;
    if (!chat.lastMessage && !chat.lastMessageText && !chat.lastMessageAt) return 0;

    const lastSender = chat.lastSenderId || chat.lastSenderID || chat.lastMessageSenderId || '';
    if (lastSender === user.uid) return 0;

    const rawLMA = chat.lastMessageAt;
    if (!rawLMA) return 0;

    const toMs = (v) => {
      if (!v) return 0;
      if (typeof v.toMillis === 'function') return v.toMillis();
      if (typeof v.toDate === 'function') return v.toDate().getTime();
      if (typeof v.seconds === 'number') return v.seconds * 1000;
      if (v instanceof Date) return v.getTime();
      if (typeof v === 'number') return v;
      const n = new Date(v).getTime();
      return isNaN(n) ? 0 : n;
    };

    const lmaMs = toMs(rawLMA);
    if (!lmaMs) return 0;

    const readByMap = chat.lastReadAtBy || {};
    const readMs = toMs(readByMap[user.uid]);

    if (!readMs) return 1;
    return lmaMs > readMs ? 1 : 0;
  };

  // Get other participant
  const getOtherParticipant = (chat) => {
    if (!chat || !chat.participantsData || !user) return null;
    return chat.participantsData.find(p => p.uid !== user.uid);
  };

  // Get profile image URL with fallback
  // Priority: profileImageUrl (from "mijn profiel") > profileImageURL > avatarURL > photoURL
  const getProfileImageUrl = (participant) => {
    if (!participant) {
      console.log('getProfileImageUrl: No participant provided');
      return null;
    }
    
    // Check all possible field names for profile image
    // Priority: profileImageUrl (from "mijn profiel") > logoURL (for landlords) > profileImageURL > avatarURL > photoURL
    const imageUrl = participant.profileImageUrl ||  // From "mijn profiel" - highest priority
           participant.logoURL ||  // For landlords
           participant.logoUrl ||  // For landlords (lowercase variant)
           participant.profileImageURL || 
           participant.avatarURL || 
           participant.avatarUrl ||
           participant.photoURL || 
           participant.photoUrl ||
           null;
    
    console.log('getProfileImageUrl for', participant.displayName || participant.uid, ':', {
      profileImageUrl: participant.profileImageUrl,
      profileImageURL: participant.profileImageURL,
      avatarURL: participant.avatarURL,
      photoURL: participant.photoURL,
      result: imageUrl
    });
    
    // Return the URL if it's a valid string
    const result = imageUrl && typeof imageUrl === 'string' && imageUrl.trim() !== '' ? imageUrl : null;
    console.log('Final image URL result:', result);
    return result;
  };

  // Get user initials for avatar
  const getUserInitials = (userData) => {
    if (!userData) return '?';
    const name = userData.displayName || userData.name || userData.email || '';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name[0]?.toUpperCase() || '?';
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Niet ingelogd</h2>
          <p className="text-gray-600">Je moet ingelogd zijn om berichten te bekijken.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <div className="container-professional py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Berichten</h1>
              <p className="text-gray-600">
                Communiceer met verhuurders over je favoriete kamers
              </p>
            </div>
          </div>
        </div>

        {/* Main Chat Interface */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden h-[calc(100vh-200px)]">
          <div className="flex h-full">
            {/* Chat List Sidebar */}
            <div className="w-1/3 border-r border-gray-200 flex flex-col bg-gray-50">
              {/* Search */}
              <div className="p-4 border-b border-gray-200 bg-white">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Zoek gesprekken..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ios-blue focus:border-transparent"
                  />
                </div>
              </div>

              {/* Chat List */}
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="p-4 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ios-blue mx-auto mb-2"></div>
                    <p className="text-sm text-gray-600">Gesprekken laden...</p>
                  </div>
                ) : filteredChats.length === 0 ? (
                  <div className="p-6 text-center">
                    <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Geen gesprekken</h3>
                    <p className="text-gray-600">Begin een gesprek met een verhuurder</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {filteredChats.map((chat) => {
                      const otherParticipant = getOtherParticipant(chat);
                      const unreadCount = getUnreadCount(chat);
                      const isSelected = selectedChat?.id === chat.id;

                      return (
                        <div
                          key={chat.id}
                          onClick={() => setSelectedChat(chat)}
                          className={`p-4 cursor-pointer transition-all hover:bg-gray-100 ${
                            isSelected ? 'bg-blue-50 border-l-4 border-ios-blue' : ''
                          }`}
                        >
                          <div className="flex items-start space-x-3">
                            {/* Avatar */}
                            <div className="relative flex-shrink-0">
                              <div className="w-12 h-12 bg-gradient-to-br from-ios-blue to-ios-blue-dark rounded-full overflow-hidden relative">
                                {(() => {
                                  const imageUrl = getProfileImageUrl(otherParticipant);
                                  console.log('Rendering avatar for', otherParticipant?.displayName, 'with imageUrl:', imageUrl);
                                  if (imageUrl) {
                                    return (
                                      <img 
                                        src={imageUrl} 
                                        alt={otherParticipant?.displayName || otherParticipant?.name || 'User'}
                                        className="w-full h-full object-cover absolute inset-0"
                                        onError={(e) => {
                                          console.error('Image failed to load:', imageUrl, e);
                                          // Fallback to initials if image fails to load
                                          e.target.style.display = 'none';
                                          const fallback = e.target.nextElementSibling;
                                          if (fallback) fallback.style.display = 'flex';
                                        }}
                                        onLoad={() => {
                                          console.log('Image loaded successfully:', imageUrl);
                                        }}
                                      />
                                    );
                                  }
                                  return null;
                                })()}
                                <div 
                                  className={`w-full h-full rounded-full flex items-center justify-center text-white font-semibold text-sm absolute inset-0 ${getProfileImageUrl(otherParticipant) ? 'hidden' : ''}`}
                                  style={{ zIndex: getProfileImageUrl(otherParticipant) ? -1 : 1 }}
                                >
                                  {getUserInitials(otherParticipant)}
                                </div>
                              </div>
                              {unreadCount > 0 && (
                                <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                                  {unreadCount > 9 ? '9+' : unreadCount}
                                </div>
                              )}
                            </div>

                            {/* Chat Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <h3 className="font-semibold text-gray-900 truncate">
                                  {otherParticipant?.displayName || otherParticipant?.name || otherParticipant?.contactName || otherParticipant?.companyName || otherParticipant?.email || 'Onbekende gebruiker'}
                                </h3>
                                {chat.lastMessageAt && (
                                  <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                                    {formatTime(chat.lastMessageAt)}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center space-x-2 mb-1">
                                {chat.propertyTitle && (
                                  <p className="text-xs text-gray-500 truncate flex items-center">
                                    <Building2 className="w-3 h-3 mr-1 flex-shrink-0" />
                                    {chat.propertyTitle}
                                  </p>
                                )}
                              </div>
                              <p className={`text-sm truncate ${unreadCount > 0 ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                                {chat.lastMessage || 'Geen berichten'}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Chat View */}
            <div className="flex-1 flex flex-col bg-white">
              {selectedChat ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-ios-blue to-ios-blue-dark text-white">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-white/20 rounded-full overflow-hidden relative">
                          {(() => {
                            const imageUrl = getProfileImageUrl(otherUser);
                            if (imageUrl) {
                              return (
                                <img 
                                  src={imageUrl} 
                                  alt={otherUser?.displayName || otherUser?.name || 'User'}
                                  className="w-full h-full object-cover absolute inset-0"
                                  onError={(e) => {
                                    console.error('Image failed to load:', imageUrl);
                                    e.target.style.display = 'none';
                                    const fallback = e.target.nextElementSibling;
                                    if (fallback) fallback.style.display = 'flex';
                                  }}
                                  onLoad={() => console.log('Image loaded:', imageUrl)}
                                />
                              );
                            }
                            return null;
                          })()}
                          <div 
                            className={`w-full h-full rounded-full flex items-center justify-center text-white font-semibold absolute inset-0 ${getProfileImageUrl(otherUser) ? 'hidden' : ''}`}
                            style={{ zIndex: getProfileImageUrl(otherUser) ? -1 : 1 }}
                          >
                            {getUserInitials(otherUser)}
                          </div>
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">
                            {otherUser?.displayName || otherUser?.name || otherUser?.contactName || otherUser?.companyName || otherUser?.email || 'Onbekende gebruiker'}
                          </h3>
                          {property && (
                            <p className="text-sm text-white/80 flex items-center">
                              <Building2 className="w-3 h-3 mr-1" />
                              {property.titel || property.title || property.naam || 'Pand'}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                          <MoreVertical className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <div 
                    ref={messagesContainerRef}
                    className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50"
                  >
                    {messages.length === 0 ? (
                      <div className="text-center py-12">
                        <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Geen berichten</h3>
                        <p className="text-gray-600">Stuur een bericht om het gesprek te beginnen</p>
                      </div>
                    ) : (
                      messages.map((message, index) => {
                        const isOwnMessage = message.senderId === user.uid;
                        const showAvatar = index === 0 || messages[index - 1].senderId !== message.senderId;
                        const showDate = index === 0 || 
                          new Date(message.createdAt).toDateString() !== new Date(messages[index - 1].createdAt).toDateString();

                        return (
                          <React.Fragment key={message.id}>
                            {showDate && (
                              <div className="flex justify-center my-4">
                                <span className="px-3 py-1 bg-gray-200 rounded-full text-xs text-gray-600">
                                  {new Date(message.createdAt).toLocaleDateString('nl-NL', {
                                    weekday: 'long',
                                    day: 'numeric',
                                    month: 'long'
                                  })}
                                </span>
                              </div>
                            )}
                            <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} ${showAvatar ? 'mb-2' : 'mb-1'}`}>
                              {!isOwnMessage && showAvatar && (
                                <div className="w-8 h-8 bg-gradient-to-br from-ios-blue to-ios-blue-dark rounded-full overflow-hidden mr-2 flex-shrink-0 relative">
                                  {(() => {
                                    const imageUrl = getProfileImageUrl(otherUser);
                                    if (imageUrl) {
                                      return (
                                        <img 
                                          src={imageUrl} 
                                          alt={otherUser?.displayName || otherUser?.name || 'User'}
                                          className="w-full h-full object-cover absolute inset-0"
                                          onError={(e) => {
                                            console.error('Image failed to load:', imageUrl);
                                            e.target.style.display = 'none';
                                            const fallback = e.target.nextElementSibling;
                                            if (fallback) fallback.style.display = 'flex';
                                          }}
                                          onLoad={() => console.log('Image loaded:', imageUrl)}
                                        />
                                      );
                                    }
                                    return null;
                                  })()}
                                  <div 
                                    className={`w-full h-full rounded-full flex items-center justify-center text-white text-xs font-semibold absolute inset-0 ${getProfileImageUrl(otherUser) ? 'hidden' : ''}`}
                                    style={{ zIndex: getProfileImageUrl(otherUser) ? -1 : 1 }}
                                  >
                                    {getUserInitials(otherUser)}
                                  </div>
                                </div>
                              )}
                              <div className={`max-w-md ${isOwnMessage ? 'order-2' : 'order-1'}`}>
                                {!isOwnMessage && showAvatar && (
                                  <p className="text-xs text-gray-500 mb-1 ml-1">
                                    {otherUser?.displayName || otherUser?.name || otherUser?.contactName || otherUser?.companyName || otherUser?.email || 'Onbekende gebruiker'}
                                  </p>
                                )}
                                <div className={`rounded-2xl px-4 py-2 ${
                                  isOwnMessage
                                    ? 'bg-gradient-to-r from-ios-blue to-ios-blue-dark text-white rounded-br-sm'
                                    : 'bg-white text-gray-900 border border-gray-200 rounded-bl-sm shadow-sm'
                                }`}>
                                  <p className="text-sm whitespace-pre-wrap break-words">{message.text}</p>
                                  <div className={`flex items-center justify-end mt-1 space-x-1 ${
                                    isOwnMessage ? 'text-white/70' : 'text-gray-400'
                                  }`}>
                                    <span className="text-xs">
                                      {formatMessageTime(message.createdAt)}
                                    </span>
                                    {isOwnMessage && (
                                      <CheckCircle2 className={`w-3 h-3 ${message.read ? 'text-blue-300' : ''}`} />
                                    )}
                                  </div>
                                </div>
                              </div>
                              {isOwnMessage && showAvatar && (
                                <div className="w-8 h-8 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full flex items-center justify-center text-white text-xs font-semibold ml-2 flex-shrink-0 order-3">
                                  {getUserInitials({ displayName: user.displayName || user.email })}
                                </div>
                              )}
                            </div>
                            <div ref={messagesEndRef} />
                          </React.Fragment>
                        );
                      })
                    )}
                  </div>

                  {/* Message Input */}
                  <div className="border-t border-gray-200 p-4 bg-white">
                    <form onSubmit={handleSendMessage} className="flex items-end space-x-2">
                      <div className="flex-1">
                        <textarea
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendMessage(e);
                            }
                          }}
                          placeholder="Type een bericht..."
                          rows={1}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ios-blue focus:border-transparent resize-none"
                          style={{ minHeight: '44px', maxHeight: '120px' }}
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <Paperclip className="w-5 h-5" />
                        </button>
                        <button
                          type="button"
                          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <Smile className="w-5 h-5" />
                        </button>
                        <button
                          type="submit"
                          disabled={!newMessage.trim()}
                          className="px-4 py-3 bg-gradient-to-r from-ios-blue to-ios-blue-dark text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        >
                          <Send className="w-5 h-5" />
                        </button>
                      </div>
                    </form>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center bg-gray-50">
                  <div className="text-center">
                    <MessageCircle className="w-24 h-24 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Selecteer een gesprek</h3>
                    <p className="text-gray-600">Kies een gesprek uit de lijst om berichten te bekijken</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

